import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, ArrowLeft, Search } from 'lucide-react';
import Button from '@components/common/Button';

const NotFoundPage = () => {
  useEffect(() => {
    document.title = '404 - Page Not Found | RDX Ticketing System';
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center p-6">
      <motion.div
        className="max-w-md w-full text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* 404 Illustration */}
        <motion.div
          className="mb-8"
          initial={{ scale: 0.85 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 180 }}
        >
          <div className="relative">
            <div className="text-9xl font-extrabold text-primary-100 drop-shadow-sm select-none">
                            404
            </div>
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <Search className="w-16 h-16 text-primary-400 animate-pulse-slow" />
            </motion.div>
          </div>
        </motion.div>

        {/* Heading */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
                        Page Not Found
          </h1>
          <p className="text-gray-600 leading-relaxed mb-8">
                        Sorry, we couldn't find the page you're looking for.<br />
                        It may have been moved, deleted, or never existed.
          </p>
        </motion.div>

        {/* Actions */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <Link to="/" aria-label="Go to Home Page">
            <Button
              variant="primary"
              leftIcon={<Home className="w-4 h-4" />}
              className="w-full sm:w-auto"
            >
                            Go Home
            </Button>
          </Link>

          <Button
            variant="outline"
            leftIcon={<ArrowLeft className="w-4 h-4" />}
            onClick={() => window.history.back()}
            className="w-full sm:w-auto"
            aria-label="Go Back"
          >
                        Go Back
          </Button>
        </motion.div>

        {/* Helpful Links */}
        <motion.div
          className="mt-12 pt-8 border-t border-gray-200"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          <p className="text-sm text-gray-500 mb-4">
                        Looking for something specific?
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <Link
              to="/tickets"
              className="text-primary-600 hover:text-primary-700 transition-colors underline-offset-2 hover:underline"
            >
                            View Tickets
            </Link>
            <Link
              to="/tickets/create"
              className="text-primary-600 hover:text-primary-700 transition-colors underline-offset-2 hover:underline"
            >
                            Create Ticket
            </Link>
            <Link
              to="/profile"
              className="text-primary-600 hover:text-primary-700 transition-colors underline-offset-2 hover:underline"
            >
                            My Profile
            </Link>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default NotFoundPage;
